// src/pages/GroupsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Users, RefreshCw } from 'lucide-react';
import { groupsAPI } from '../api/groupsApi';
import GroupCard from '../components/Groups/GroupCard';
import './GroupsPage.css';

const GroupsPage = () => {
  const navigate = useNavigate();
  const [myGroups, setMyGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingAll, setLoadingAll] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchMyGroups = useCallback(async () => {
    setLoadingMy(true);
    try {
      const data = await groupsAPI.getMyGroups();
      setMyGroups(Array.isArray(data) ? data : data.results ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMy(false);
    }
  }, []);

  const fetchAllGroups = useCallback(async () => {
    setLoadingAll(true);
    try {
      const params = {};
      if (debouncedQuery) params.search = debouncedQuery;
      const data = await groupsAPI.getAllGroups(params);
      const list = Array.isArray(data) ? data : data.results ?? [];
      // Exclude groups the user is already a member of from "Discover" section
      const myIds = new Set(myGroups.map((g) => g.id));
      setAllGroups(list.filter((g) => !myIds.has(g.id)));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAll(false);
    }
  }, [debouncedQuery, myGroups]);

  useEffect(() => { fetchMyGroups(); }, [fetchMyGroups]);
  useEffect(() => { fetchAllGroups(); }, [fetchAllGroups]);

  const handleGroupClick = (group) => {
    navigate(`/groups/${group.id}`);
  };

  const handleMyGroupsBlock = () => {
    navigate('/groups/my');
  };

  const handleJoinGroup = async (group, e) => {
    e.stopPropagation(); // don't navigate to group detail
    try {
      const result = await groupsAPI.joinGroup(group.id);
      if (result.status === 'joined') {
        // Move to myGroups immediately
        setMyGroups((prev) => [...prev, result.group]);
        setAllGroups((prev) => prev.filter((g) => g.id !== group.id));
      } else if (result.status === 'pending') {
        // Update the card to show pending state
        setAllGroups((prev) =>
          prev.map((g) =>
            g.id === group.id ? { ...g, join_status: 'pending' } : g
          )
        );
      }
    } catch (e) {
      console.error('Failed to join group:', e);
    }
  };

  return (
    <div className="groups-page">
      {/* Page header */}
      <div className="groups-page__header">
        <div>
          <h1 className="groups-page__title">Research Groups</h1>
          <p className="groups-page__subtitle">
            Collaborate with colleagues by sharing experiments and analyses.
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() => navigate('/groups/create')}
        >
          <Plus size={16} /> Create New
        </button>
      </div>

      {/* My Groups summary block */}
      {myGroups.length > 0 && (
        <section className="groups-page__section">
          <div className="groups-page__section-header">
            <h2 className="groups-page__section-title">
              <Users size={18} /> My Groups
            </h2>
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleMyGroupsBlock}
            >
              View all →
            </button>
          </div>

          <div
            className="groups-page__my-block"
            onClick={handleMyGroupsBlock}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleMyGroupsBlock()}
          >
            {loadingMy ? (
              <div className="groups-page__loading">
                <RefreshCw size={20} className="spinning" />
              </div>
            ) : (
              <>
                <div className="groups-page__my-avatars">
                  {myGroups.slice(0, 5).map((g) => (
                    <div key={g.id} className="groups-page__my-avatar" title={g.name}>
                      {g.avatar ? (
                        <img src={g.avatar} alt={g.name} />
                      ) : (
                        <div className="groups-page__my-avatar-placeholder">
                          {g.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                  {myGroups.length > 5 && (
                    <div className="groups-page__my-avatar groups-page__my-avatar--more">
                      +{myGroups.length - 5}
                    </div>
                  )}
                </div>
                <div className="groups-page__my-info">
                  <span className="groups-page__my-count">
                    {myGroups.length} group{myGroups.length !== 1 ? 's' : ''}
                  </span>
                  <span className="groups-page__my-hint">Click to manage your groups</span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Discover / All groups */}
      <section className="groups-page__section">
        <div className="groups-page__section-header">
          <h2 className="groups-page__section-title">
            {myGroups.length > 0 ? 'Discover Groups' : 'All Groups'}
          </h2>
        </div>

        {/* Search */}
        <div className="groups-page__search">
          <Search size={16} className="groups-page__search-icon" />
          <input
            type="text"
            className="groups-page__search-input"
            placeholder="Search groups by name or description…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loadingAll ? (
          <div className="groups-page__loading">
            <RefreshCw size={24} className="spinning" />
          </div>
        ) : allGroups.length === 0 ? (
          <div className="groups-page__empty">
            <Users size={40} />
            <p>
              {debouncedQuery
                ? `No groups found for "${debouncedQuery}"`
                : 'No other groups to discover.'}
            </p>
            <button
              className="btn btn--primary"
              onClick={() => navigate('/groups/create')}
            >
              <Plus size={14} /> Create the first one
            </button>
          </div>
        ) : (
          <div className="groups-page__grid">
            {allGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onClick={handleGroupClick}
                onJoin={group.visibility === 'public' ? handleJoinGroup : undefined}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default GroupsPage;
